import { flow, Instance, types, getEnv, cast } from "mobx-state-tree";

import { ProjectCodeSystemResponse } from "../../api-models/response/ProjectCodeSystemResponse";
import { ProjectFormatResponse } from "../../api-models/response/ProjectFormatResponse";
import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import Language from "../translation/language";
import { translationService } from "../translation/TranslationService";
import { ProjectCodeSystem } from "./ProjectCodeSystem";
import { ProjectFormat } from "./ProjectFormat";

export const emptyProjectFormat = new ProjectFormat(
  {
    id: "",
    name: "",
    language: "",
    readonly: false,
  },
  false
);

export const emptyProjectCodeSystem = new ProjectCodeSystem(
  {
    id: "",
    name: "",
    language: "",
    readonly: false,
  },
  false
);

const ProjectFormatCodeSystemModel = types.model({
  selectedProjectFormat: types.optional(types.frozen<ProjectFormat>(), emptyProjectFormat),
  projectFormats: types.array(types.frozen<ProjectFormat>()),

  selectedProjectCodeSystem: types.optional(
    types.frozen<ProjectCodeSystem>(),
    emptyProjectCodeSystem
  ),
  codeSystems: types.array(types.frozen<ProjectCodeSystem>()),

  codeSystemDisabled: true,
  newCodeSystemsRequired: false,
});

export const ProjectFormatCodeSystemServiceModel = ProjectFormatCodeSystemModel
  .named("ProjectFormatCodeSystemServiceModel")
  .actions((self) => ({
    setSelectedProjectFormat(newProjectFormat: ProjectFormat) {
      self.selectedProjectFormat = newProjectFormat;
      self.codeSystemDisabled = false;
      self.newCodeSystemsRequired = true;
    },
    setSelectedProjectCodeSystem(newCodeSystem: ProjectCodeSystem) {
      self.selectedProjectCodeSystem = newCodeSystem;
    },
    setCodeSystemsLoadingOption() {
      const loadingOption = new ProjectCodeSystem(
        {
          id: "",
          name: `${translationService.translate("general.loading")}...`,
          language: Language.german,
          readonly: false,
        },
        false
      );
      self.codeSystems = cast([loadingOption]);
    },
    codeSystemsReceived() {
      if (self.selectedProjectCodeSystem.id === emptyProjectCodeSystem.id)
        self.selectedProjectCodeSystem = emptyProjectCodeSystem;

      if (
        !self.codeSystems.find((p: ProjectCodeSystem) => p.id === self.selectedProjectCodeSystem.id)
      )
        self.selectedProjectCodeSystem = emptyProjectCodeSystem;
    },
    getFormats: flow(function* getFormats() {
      const endpoint = endpoints.get("projects_formats");

      try {
        const response = yield getEnv(self).api.get(endpoint);
        self.projectFormats = response.map(
          (projectFormatsResponse: ProjectFormatResponse) =>
            new ProjectFormat(projectFormatsResponse, true)
        );
      } catch (error) {
        const applicationError = error as ApplicationError;
        errorHandler.handleErrorInSnackbar(applicationError);
      }
    }),
    getFormatCodeSystems: flow(function* getFormatCodeSystems(formatId: string) {
      const endpoint = endpoints.get("projects_code_systems");
      endpoint.queryParams = serializeToQueryParams({ formatId });
      try {
        const response = yield getEnv(self).api.get(endpoint);

        self.codeSystems = response.map(
          (projectCodeSystemsResponse: ProjectCodeSystemResponse) =>
            new ProjectCodeSystem(projectCodeSystemsResponse, true)
        );
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.project.codeSystems");
        errorHandler.handleErrorInSnackbar(applicationError);
      }
    }),
  }))
  .actions((self) => ({
    setupCreateProjectFormatAndCodeSystemInitialData: flow(function* loadFormats() {
      yield self.getFormats();
    }),
    setupEditProjectFormatAndCodeSystemInitialData: flow(function* loadFormatsWithRelatedCodeSystem(
      currentProjectFormat: ProjectFormatResponse,
      currentProjectCodeSystem: ProjectCodeSystemResponse
    ) {
      yield self.getFormats();

      if (currentProjectFormat.id) {
        self.selectedProjectFormat = new ProjectFormat(currentProjectFormat, true);
        self.codeSystemDisabled = false;
      } else {
        self.selectedProjectFormat = emptyProjectFormat;
        self.codeSystemDisabled = true;
      }

      yield self.getFormatCodeSystems(self.selectedProjectFormat.id);

      if (currentProjectCodeSystem.id) {
        self.selectedProjectCodeSystem = new ProjectCodeSystem(currentProjectCodeSystem, true);
        self.codeSystemDisabled = false;
      } else {
        self.selectedProjectCodeSystem = emptyProjectCodeSystem;
        self.codeSystemDisabled = true;
      }
    }),
    renewCodeSystems: flow(function* renewCodeSystems(formatId: string) {
      self.setCodeSystemsLoadingOption();
      yield self.getFormatCodeSystems(formatId);
      self.codeSystemsReceived();
    }),
  }))
  .views((self) => ({
    get formatsAreLoading() {
      return !self.projectFormats.length;
    },
    get codeSystemsAreLoading() {
      return !self.codeSystems.length;
    },
  }));

export type ProjectFormatCodeSystemService = Instance<typeof ProjectFormatCodeSystemServiceModel>;
